import { useEffect, useMemo, useRef, useState } from "react";

import { formatTimelineDate } from "@pages/Mentoring/helpers/mentoringTimeline";

import api from "@helpers/api";

const MONTHS_BEFORE_START = 6;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDateOnly(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Community revenue around a mentoring (§6.11). For an enrollment with an
 * attached community and a start date, it loads the community's monthly revenue
 * from "start − 6 months" up to today, and builds a deep-link into /insights
 * with those same filters prefilled. Returns `available: false` for enrollments
 * with no community / no start date (pending · awaiting_community), where there's
 * nothing to chart yet.
 */
export default function useMentoringRevenue(enrollment) {
  const community = enrollment?.community ?? null;
  const communityId = community?.id ?? null;
  const startedAt = enrollment?.timestamps?.started_at ?? null;

  // Monthly window: the calendar month 6 months before the start → today.
  const range = useMemo(() => {
    if (!startedAt) return null;
    const start = new Date(startedAt);
    if (Number.isNaN(start.getTime())) return null;
    const from = new Date(start.getFullYear(), start.getMonth() - MONTHS_BEFORE_START, 1, 0, 0, 0, 0);
    const now = new Date();
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 0);
    return { from, to };
  }, [startedAt]);

  const available = !!communityId && !!range;

  // Deep-link into /insights with the same community + custom monthly range, so
  // the full insights page opens already filtered. Plans are omitted → insights
  // defaults to "all plans".
  const insightsHref = useMemo(() => {
    if (!available) return null;
    const params = new URLSearchParams();
    params.set("community", String(communityId));
    params.set("preset", "custom");
    params.set("from", toDateOnly(range.from));
    params.set("to", toDateOnly(range.to));
    params.set("granularity", "month");
    return `/insights?${params.toString()}`;
  }, [available, communityId, range]);

  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!available) {
      setRevenue(null);
      setLoading(false);
      return;
    }

    const reqId = ++reqRef.current;
    setLoading(true);

    (async () => {
      try {
        // Revenue must count archived (disabled) plans as well — they keep renewing.
        const plansRes = await api.get(`community/${communityId}/plan`, { include_deleted: true, include_disabled: true });
        const planIds = (Array.isArray(plansRes?.data) ? plansRes.data : []).map((p) => p.id);

        const params = new URLSearchParams();
        params.append("date_from", `${toDateOnly(range.from)} 00:00:00`);
        params.append("date_to", `${toDateOnly(range.to)} 23:59:59`);
        params.append("granularity_type", "month");
        params.append("granularity_amount", "1");
        planIds.forEach((id) => params.append("plan_ids[]", id));

        const reportRes = await api.get(`community/${communityId}/insights/salesReport?${params.toString()}`);

        if (reqId !== reqRef.current) return;
        setRevenue(reportRes?.data?.revenue ?? null);
      } catch (e) {
        if (reqId !== reqRef.current) return;
        console.log(e);
        setRevenue(null);
      }
      if (reqId === reqRef.current) setLoading(false);
    })();
  }, [available, communityId, range]);

  return {
    available,
    loading,
    series: revenue,
    communityName: community?.name ?? "",
    startDateLabel: startedAt ? formatTimelineDate(startedAt) : "",
    referenceDate: startedAt,
    insightsHref,
  };
}
